import { useEffect, useLayoutEffect, useMemo, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import MemberShell from "@/components/MemberShell";
import { MEMBER_DASH_NAV_BASE } from "@/lib/memberDashboardNav";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { messageCoordonneesInterdites, texteContientCoordonnees } from "@/lib/contactGuard";
import { cn } from "@/lib/utils";
import { FR_DEPARTEMENTS, countryToIso2 } from "@/lib/departements";
import { findDepartementFieldRaw, formatDepartmentDisplay } from "@/lib/departementDisplay";
import { SELLER_PAYOUT_HOURS } from "@/lib/businessDays";
import VenteListingDisclaimerDialog from "@/components/VenteListingDisclaimerDialog";
import {
  deriveAdminCodeFromPostcode,
  deriveFrDepartementFromCpWithDetails,
  extractPostcodeFromAddress,
  syncDeptFieldsFromAddressIntl,
} from "@/lib/intlAdminFromPostcode";
import { formatPhoneInputLive, isPhoneLikeChampName, tryNormalizePhoneToE164 } from "@/lib/phoneFormat";
import { findInvalidPhoneInFiche, normalizePhoneFieldsInDossierPayload } from "@/lib/dossierPhoneNormalize";
import {
  sortProduitChampsForDisplay,
  isAutreSituationProValue,
  isCelibataireMaritalValue,
  isSituationMaritaleField,
  isSituationProListField,
  isSituationProPrecisionField,
  precisionFieldNameForSituationPro,
  shouldHideSituationProField,
} from "@/lib/produitChampsForm";

const labelClass = "text-slate-800 text-sm sm:text-[15px] font-semibold";
const helpClass = "text-slate-600 text-sm leading-relaxed";
const calloutAmber = "text-sm text-amber-900/90 bg-amber-50/90 rounded-xl py-3 px-3.5 border border-amber-200/80";
const filtreVendeurInfo =
  "text-sm text-slate-700 bg-white rounded-2xl border border-slate-200/90 py-3.5 px-4 leading-snug shadow-sm";
const infoLine = "text-sm text-slate-600 py-2.5 px-3.5 rounded-xl bg-[#2D7FF9]/[0.06] border border-[#2D7FF9]/10";
/** Chaque question de fiche = carte distincte, pas l’effet “liste de lignes” */
const ficheChampClass =
  "rounded-2xl border border-slate-200/90 bg-white p-4 sm:p-5 shadow-sm space-y-3 sm:space-y-3.5";

function normalizeChampKey(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

function isContactLikeChamp(nom) {
  const k = normalizeChampKey(nom);
  return (
    k === "nom du client" ||
    k === "nom du client/prospect" ||
    k === "telephone du client" ||
    k === "telephone du client/prospect" ||
    k === "tel du client" ||
    k === "adresse du client" ||
    k === "adresse client"
  );
}

/**
 * Bloc de formulaire (séparation par le parent `divide-y`).
 * @param {"default"|"muted"} variant
 */
function FormBlock({ n, title, children, className = "", variant = "default" }) {
  const hasTitle = title != null && String(title).trim() !== "";
  const showNumber = n != null && n !== false;
  return (
    <section
      className={cn(
        "px-3 py-4 sm:px-5 sm:py-5",
        variant === "muted" && "bg-slate-50/50",
        className
      )}
      aria-labelledby={hasTitle ? `dossier-step-${showNumber ? n : "t"}` : undefined}
      aria-label={!hasTitle && showNumber ? `Étape ${n} du formulaire` : undefined}
    >
      {hasTitle && (
        <h2
          id={`dossier-step-${showNumber ? n : "t"}`}
          className="mb-2.5 flex items-center gap-2 font-display text-sm sm:text-[15px] font-bold leading-snug text-[#0B0D12]"
        >
          {showNumber && (
            <span
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2D7FF9]/12 text-[11px] font-bold text-[#2D7FF9] tabular-nums"
              aria-hidden
            >
              {n}
            </span>
          )}
          <span>{title}</span>
        </h2>
      )}
      <div className="space-y-2.5 sm:space-y-3">{children}</div>
    </section>
  );
}

/** Remplit la localisation et le contact côté base, à partir des réponses de la fiche (évite de redemander la même chose ailleurs). */
function deriveVenteDossierDepuisFiche(customFields, champs) {
  const str = (v) => (v != null && v !== "" && String(v).trim() ? String(v).trim() : null);
  const list = (champs || []).filter((c) => c?.nom);
  const byKey = (k) => str(customFields?.[k]);
  const locationKey = (k) =>
    /locali|g[ée]o|ville|r[ée]g(ion)?|d[ée]part|départ|zone|adresse|postal|^\s*cp\s*$/i.test(k) ||
    /^département/i.test(k);
  const nameKey = (k) => {
    const n = k.trim();
    if (/tél|phone|portable|num[ée]ro|mobile|fax|mail|@/i.test(n)) return false;
    return /^(nom|pr[ée]nom|client|prospect|contact|foyer|ménage)/i.test(n);
  };
  let location = null;
  for (const c of list) {
    const v = byKey(c.nom);
    if (v && locationKey(c.nom)) {
      location = v;
      break;
    }
  }
  if (!location) {
    for (const c of list) {
      if (c.type !== "boolean" && (c.type === "text" || c.type === "list" || c.type === "number")) {
        const v = byKey(c.nom);
        if (v) {
          location = v;
          break;
        }
      }
    }
  }
  if (!location) location = "—";

  let client_name = null;
  for (const c of list) {
    const v = byKey(c.nom);
    if (v && nameKey(c.nom) && c.type !== "number") {
      client_name = v;
      break;
    }
  }

  let client_phone = null;
  for (const c of list) {
    const v = byKey(c.nom);
    if (v && isPhoneLikeChampName(c.nom)) {
      client_phone = tryNormalizePhoneToE164(v) || String(v).trim();
      break;
    }
  }

  return { location, client_name: client_name || undefined, client_phone: client_phone || undefined };
}

/** Tous les champs de fiche doivent être remplis (hors listes vides côté admin = pas de saisie possible). */
function isPrecisionFieldName(name) {
  const n = String(name || "");
  if (isSituationProPrecisionField(n)) return true;
  return /pr[ée]cisions?/i.test(n);
}

function messageFicheIncomplete(customFields, champs) {
  const cf = customFields && typeof customFields === "object" ? customFields : {};
  const hasAddressFieldInSchema = (champs || []).some((c) => /adresse|address/i.test(String(c?.nom || "")));
  const countryKey = Object.keys(cf).find((k) => /pays|country/i.test(String(k)));
  const countryVal = countryKey ? cf[countryKey] : null;
  const iso2 = countryToIso2(countryVal);
  for (const c of champs || []) {
    if (!c?.nom) continue;
    if (shouldHideSituationProField(c.nom, cf, champs)) continue;
    // Les champs "précisions" sont toujours optionnels (achat et vente).
    if (isPrecisionFieldName(c.nom)) continue;
    // Champs contact: jamais bloquants.
    if (isContactLikeChamp(c.nom)) continue;
    // Si la fiche contient un champ adresse, le département est dérivé automatiquement.
    if (hasAddressFieldInSchema && /département|departement/i.test(String(c.nom))) {
      continue;
    }
    if (c.type === "text" || c.type === "number") {
      const v = cf?.[c.nom];
      if (v == null || String(v).trim() === "") {
        return `Remplissez le champ « ${c.nom} ».`;
      }
    }
    if (c.type === "list") {
      const opts = Array.isArray(c.options) ? c.options.map((o) => String(o).trim()).filter(Boolean) : [];
      if (opts.length === 0) continue;
      const v = cf?.[c.nom];
      if (v == null || String(v).trim() === "") {
        return `Choisissez une valeur pour « ${c.nom} ».`;
      }
      if (isSituationProListField(c.nom) && isAutreSituationProValue(v)) {
        const precNom = precisionFieldNameForSituationPro(c.nom);
        const prec = precNom ? cf?.[precNom] : null;
        if (prec == null || String(prec).trim() === "") {
          return `Précisez la situation professionnelle pour « ${c.nom} » (Autre).`;
        }
      }
    }
  }
  return null;
}

const NOUVEAU_DOSSIER_DRAFT_KEY = "rdm_nouveau_dossier_draft_v1";

/** Valeur affichée dans un input décimal : 0 = champ vide (évite « 05 » à la saisie). */
function decimalFieldDisplay(value) {
  if (value === null || value === undefined || value === "") return "";
  if (value === 0 || value === "0") return "";
  return String(value);
}

function saveNouveauDossierDraft(payload) {
  try {
    sessionStorage.setItem(NOUVEAU_DOSSIER_DRAFT_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

function loadNouveauDossierDraft() {
  try {
    const raw = sessionStorage.getItem(NOUVEAU_DOSSIER_DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearNouveauDossierDraft() {
  try {
    sessionStorage.removeItem(NOUVEAU_DOSSIER_DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

const NouveauDossier = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [produits, setProduits] = useState([]);
  /** Secteur choisi avant le produit (hors schéma API) */
  const [secteurFiltre, setSecteurFiltre] = useState("");
  const [form, setForm] = useState({
    produit_id: "", title: "", description: "", secteur: "", location: "", price: "", type: "vente",
    client_phone: "", client_name: "", custom_fields: {},
    price_min: "", price_max: "",
  });
  const [loading, setLoading] = useState(false);
  const [venteDisclaimerOpen, setVenteDisclaimerOpen] = useState(false);
  /** Recherche : champs pour lesquels un vendeur doit avoir la même réponse (uniquement si activé par l’utilisateur). */
  const [criteresRequisRecherche, setCriteresRequisRecherche] = useState([]);
  /** Recherche : champs choisis librement parmi ceux du produit. */
  const [champsSelectionnesRecherche, setChampsSelectionnesRecherche] = useState([]);
  const prevNomsQualifRecherche = useRef("");
  const draftRestoreUserIdRef = useRef(null);
  const [addrSuggestionsByKey, setAddrSuggestionsByKey] = useState({});
  const addrRequestIdRef = useRef(0);
  /** Ancres DOM par nom de champ (suggestions d’adresse en portail `fixed`, hors panneau scroll). */
  const addrWrapRefs = useRef({});
  const [addrSuggestOverlay, setAddrSuggestOverlay] = useState(null);

  const layoutAddrSuggestOverlay = useCallback(() => {
    setAddrSuggestOverlay((prev) => {
      if (!prev) return prev;
      const wrap = addrWrapRefs.current[prev.fieldKey];
      if (!wrap) return null;
      const r = wrap.getBoundingClientRect();
      return { ...prev, top: r.bottom + 8, left: r.left, width: r.width };
    });
  }, []);

  useLayoutEffect(() => {
    const activeKey = Object.keys(addrSuggestionsByKey).find(
      (k) => Array.isArray(addrSuggestionsByKey[k]) && addrSuggestionsByKey[k].length > 0,
    );
    if (!activeKey) {
      setAddrSuggestOverlay(null);
      return;
    }
    const wrap = addrWrapRefs.current[activeKey];
    if (!wrap) {
      setAddrSuggestOverlay(null);
      return;
    }
    const r = wrap.getBoundingClientRect();
    setAddrSuggestOverlay({ fieldKey: activeKey, top: r.bottom + 8, left: r.left, width: r.width });
  }, [addrSuggestionsByKey]);

  useEffect(() => {
    if (!addrSuggestOverlay) return undefined;
    const panel = document.querySelector('[data-testid="nouveau-dossier-scroll-panel"]');
    const onScrollOrResize = () => {
      layoutAddrSuggestOverlay();
    };
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    panel?.addEventListener("scroll", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      panel?.removeEventListener("scroll", onScrollOrResize);
    };
  }, [addrSuggestOverlay, layoutAddrSuggestOverlay]);

  useEffect(() => {
    if (!addrSuggestOverlay) return undefined;
    const onPointerDown = (e) => {
      const t = e.target;
      const wrap = addrWrapRefs.current[addrSuggestOverlay.fieldKey];
      const portal = document.querySelector('[data-testid="address-suggest-portal"]');
      if (wrap?.contains(t) || portal?.contains(t)) return;
      setAddrSuggestionsByKey((prev) => ({ ...prev, [addrSuggestOverlay.fieldKey]: [] }));
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [addrSuggestOverlay]);

  // Empêche le scroll de la page (body) : tout doit scroller dans le panel.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyOverscroll = body.style.overscrollBehavior;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.overscrollBehavior = prevBodyOverscroll;
    };
  }, []);

  useEffect(() => {
    api.get("/produits").then(({ data }) => setProduits(data));
  }, []);

  useEffect(() => {
    const uid = user?.user_id;
    if (!uid) {
      draftRestoreUserIdRef.current = null;
      return;
    }
    if (draftRestoreUserIdRef.current === uid) return;
    const d = loadNouveauDossierDraft();
    if (d?.form) {
      const restored = { ...d.form };
      if (restored.price === 0 || restored.price === "0") restored.price = "";
      setForm(restored);
      if (d.secteurFiltre != null && String(d.secteurFiltre).trim() !== "") setSecteurFiltre(String(d.secteurFiltre));
      if (Array.isArray(d.champsSelectionnesRecherche)) setChampsSelectionnesRecherche(d.champsSelectionnesRecherche);
      if (Array.isArray(d.criteresRequisRecherche)) setCriteresRequisRecherche(d.criteresRequisRecherche);
      clearNouveauDossierDraft();
      toast.success("Votre annonce en cours a été retrouvée après connexion.");
    }
    draftRestoreUserIdRef.current = uid;
  }, [user?.user_id]);

  const selectedProduit = produits.find((p) => p.produit_id === form.produit_id);

  const secteursUniques = useMemo(
    () => [...new Set(produits.map((p) => p.secteur).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr")),
    [produits]
  );
  const produitsFiltres = useMemo(() => {
    if (!secteurFiltre) return [];
    return produits.filter((p) => p.secteur === secteurFiltre);
  }, [produits, secteurFiltre]);

  const champsAffiches = useMemo(() => {
    if (!selectedProduit?.champs?.length) return [];
    const filtered = selectedProduit.champs.filter(
      (c) =>
        c.cible_annonces == null ||
        c.cible_annonces === "tous" ||
        c.cible_annonces === form.type,
    );
    return sortProduitChampsForDisplay(filtered, { produitNom: selectedProduit.nom });
  }, [selectedProduit, form.type]);

  const champsAffichesRecherche = useMemo(() => {
    if (form.type !== "recherche") return champsAffiches;
    const sel = new Set((champsSelectionnesRecherche || []).map((x) => String(x)));
    // En recherche: ne jamais demander les champs masqués (visible_public === false).
    return champsAffiches.filter((c) => c?.nom && c.visible_public !== false && sel.has(String(c.nom)));
  }, [form.type, champsAffiches, champsSelectionnesRecherche]);

  const champsSelectionnablesRecherche = useMemo(() => {
    if (form.type !== "recherche") return [];
    // En recherche, on ne propose pas les champs masqués (non affichés sur l’annonce publique)
    return champsAffiches.filter((c) => c?.nom && c.visible_public !== false && !isContactLikeChamp(c.nom));
  }, [form.type, champsAffiches]);

  // Nettoie les champs déjà sélectionnés si l'admin les a rendus masqués entre temps.
  useEffect(() => {
    if (form.type !== "recherche") return;
    const allowed = new Set(champsSelectionnablesRecherche.map((c) => String(c.nom)));
    setChampsSelectionnesRecherche((prev) => (prev || []).filter((nom) => allowed.has(String(nom))));
  }, [form.type, champsSelectionnablesRecherche]);

  const nomsChampsQualifKey = useMemo(
    () => champsAffichesRecherche.map((c) => c.nom).filter(Boolean).join("|"),
    [champsAffichesRecherche]
  );
  /** Évite d’oublier une réinit si deux produits ont les mêmes libellés de champs. */
  const signatureQualifRecherche = useMemo(
    () => (form.produit_id && form.type === "recherche" ? `${form.produit_id}::${nomsChampsQualifKey}` : ""),
    [form.produit_id, form.type, nomsChampsQualifKey]
  );

  useEffect(() => {
    if (form.type !== "recherche") {
      setCriteresRequisRecherche([]);
      setChampsSelectionnesRecherche([]);
      prevNomsQualifRecherche.current = "";
    }
  }, [form.type]);

  useEffect(() => {
    if (form.type !== "recherche" || !form.produit_id) {
      return;
    }
    // Quand on change de produit en "recherche", on repart sans champs sélectionnés.
    setChampsSelectionnesRecherche([]);
    setCriteresRequisRecherche([]);
    prevNomsQualifRecherche.current = "";
  }, [form.produit_id, form.type]);

  useEffect(() => {
    if (form.type !== "recherche" || !form.produit_id) {
      return;
    }
    if (!signatureQualifRecherche || signatureQualifRecherche === prevNomsQualifRecherche.current) {
      return;
    }
    prevNomsQualifRecherche.current = signatureQualifRecherche;
    setCriteresRequisRecherche(nomsChampsQualifKey ? nomsChampsQualifKey.split("|") : []);
  }, [form.type, form.produit_id, nomsChampsQualifKey, signatureQualifRecherche]);

  const setCritereRequisPourMatch = (nom, on) => {
    setCriteresRequisRecherche((prev) => {
      const s = new Set(prev);
      if (on) s.add(nom);
      else s.delete(nom);
      return Array.from(s);
    });
  };

  useEffect(() => {
    if (!form.produit_id) return;
    const p = produits.find((x) => x.produit_id === form.produit_id);
    if (!p?.champs) return;
    const allow = new Set(
      p.champs
        .filter(
          (c) =>
            c.cible_annonces == null ||
            c.cible_annonces === "tous" ||
            c.cible_annonces === form.type
        )
        .map((c) => c.nom)
        .filter(Boolean)
    );
    setForm((f) => {
      const next = { ...f.custom_fields };
      let ch = false;
      for (const k of Object.keys(next)) {
        if (!allow.has(k)) {
          delete next[k];
          ch = true;
        }
      }
      return ch ? { ...f, custom_fields: next } : f;
    });
  }, [form.produit_id, form.type, produits]);

  // Recherche: on n'autorise que les champs sélectionnables (non masqués).
  useEffect(() => {
    if (form.type !== "recherche") return;
    const allowed = new Set((champsSelectionnablesRecherche || []).map((c) => String(c.nom)));
    setChampsSelectionnesRecherche((prev) => {
      const base = Array.isArray(prev) ? prev : [];
      const next = base.filter((k) => allowed.has(String(k)));
      return next.length === base.length ? prev : next;
    });
  }, [form.type, champsSelectionnablesRecherche]);

  // Recherche: si un champ est retiré, on nettoie valeur + critère requis associé.
  useEffect(() => {
    if (form.type !== "recherche") return;
    const sel = new Set((champsSelectionnesRecherche || []).map((x) => String(x)));
    setForm((f) => {
      const next = { ...f.custom_fields };
      let ch = false;
      for (const k of Object.keys(next)) {
        if (!sel.has(k)) {
          delete next[k];
          ch = true;
        }
      }
      return ch ? { ...f, custom_fields: next } : f;
    });
    setCriteresRequisRecherche((prev) => prev.filter((k) => sel.has(String(k))));
  }, [form.type, champsSelectionnesRecherche]);

  /** Changer achat/vente ne remet plus le secteur choisi à zéro (moins de saut d’écran) — on vide seulement l’offre liée. */
  useEffect(() => {
    setForm((f) => ({
      ...f,
      produit_id: "",
      custom_fields: {},
      // Reset budgets when switching modes
      price: "",
      price_min: "",
      price_max: "",
    }));
  }, [form.type]);

  useEffect(() => {
    setForm((f) => ({ ...f, produit_id: "", custom_fields: {} }));
  }, [secteurFiltre]);

  const updateCustom = (key, value) => {
    setForm((f) => ({ ...f, custom_fields: { ...f.custom_fields, [key]: value } }));
  };

  const getSelectedCountryValue = useMemo(() => {
    const cf = form.custom_fields || {};
    const countryKey = Object.keys(cf).find((k) => /pays|country/i.test(String(k)));
    return countryKey ? cf[countryKey] : null;
  }, [form.custom_fields]);

  const deptOptions = useMemo(() => {
    const iso2 = countryToIso2(getSelectedCountryValue);
    if (iso2 === "fr") return FR_DEPARTEMENTS;
    return [];
  }, [getSelectedCountryValue]);

  const setDepartementFromAddressSelection = ({
    postcode,
    citycode,
    depCodeFromContext,
    addressDetails = null,
    fullAddressLine = "",
  }) => {
    const iso2 = countryToIso2(getSelectedCountryValue);
    if (!iso2) return;

    const champsListe = form.type === "recherche" ? champsAffichesRecherche : champsAffiches;
    const keysFromSchema = (champsListe || [])
      .map((c) => c?.nom)
      .filter((n) => n && /département|departement/i.test(String(n)));
    const cf = form.custom_fields || {};
    const keysFromCf = Object.keys(cf).filter((k) => /département|departement/i.test(String(k)));
    const keys = [...new Set([...keysFromSchema, ...keysFromCf])];
    if (keys.length === 0) return;

    let code = null;
    const addrLine = String(fullAddressLine || "");

    if (iso2 === "fr") {
      let c = depCodeFromContext ? String(depCodeFromContext).trim() : "";
      if (!c || !deptOptions.some((d) => String(d.code) === c)) {
        c = deriveFrDepartementFromCpWithDetails(postcode, addressDetails);
      }
      if (!c && citycode) {
        const fromCity = String(citycode).trim().slice(0, 2).toUpperCase();
        if (deptOptions.some((d) => String(d.code) === fromCity)) c = fromCity;
      }
      if (!c || !deptOptions.some((d) => String(d.code) === String(c))) return;
      code = c;
    } else if (iso2 === "gb") {
      code = deriveAdminCodeFromPostcode("gb", "", addrLine);
    } else {
      const cp = postcode || extractPostcodeFromAddress(addrLine, iso2);
      code = deriveAdminCodeFromPostcode(iso2, cp || "", addrLine);
    }

    if (!code) return;

    setForm((f) => {
      const next = { ...(f.custom_fields || {}) };
      let changed = false;
      for (const k of keys) {
        if (!next[k] || String(next[k]).trim() === "") {
          next[k] = code;
          changed = true;
        }
      }
      return changed ? { ...f, custom_fields: next } : f;
    });
  };

  const fetchAddressSuggestions = async (fieldKey, query) => {
    const q = String(query || "").trim();
    if (q.length < 3) {
      setAddrSuggestionsByKey((prev) => ({ ...prev, [fieldKey]: [] }));
      return;
    }
    const reqId = ++addrRequestIdRef.current;
    const iso2 = countryToIso2(getSelectedCountryValue);
    // Les suggestions d'adresse dépendent toujours du pays sélectionné.
    if (!iso2) {
      setAddrSuggestionsByKey((prev) => ({ ...prev, [fieldKey]: [] }));
      return;
    }
    try {
      let res;
      if (iso2 === "fr") {
        const frParams = new URLSearchParams();
        frParams.set("q", q);
        frParams.set("limit", "6");
        frParams.set("autocomplete", "1");
        res = await fetch(`https://api-adresse.data.gouv.fr/search/?${frParams.toString()}`, {
          headers: { Accept: "application/json" },
        });
      } else {
        const params = new URLSearchParams();
        params.set("format", "jsonv2");
        params.set("addressdetails", "1");
        params.set("accept-language", "fr");
        params.set("dedupe", "1");
        params.set("limit", "6");
        params.set("q", q);
        params.set("email", "contact@rdvmarket.fr");
        params.set("countrycodes", iso2);
        res = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
          headers: { Accept: "application/json" },
        });
      }
      const json = await res.json();
      if (reqId !== addrRequestIdRef.current) return;
      const toShortLabel = (addr) => {
        if (!addr || typeof addr !== "object") return null;
        const street = [addr.house_number, addr.road].filter(Boolean).join(" ").trim();
        const city = addr.city || addr.town || addr.village || addr.municipality || addr.hamlet || "";
        const cp = addr.postcode || "";
        const parts = [
          street || addr.neighbourhood || addr.suburb || addr.quarter || "",
          [cp, city].filter(Boolean).join(" ").trim(),
          addr.country || "",
        ].filter((x) => String(x || "").trim() !== "");
        return parts.join(", ");
      };

      const items = iso2 === "fr"
        ? (Array.isArray(json?.features)
            ? json.features
                .map((x) => ({
                  label: x?.properties?.label || x?.properties?.name || null,
                  place_id: x?.properties?.id || x?.properties?.label || null,
                  postcode: x?.properties?.postcode ?? null,
                  citycode: x?.properties?.citycode ?? null,
                  depCodeFromContext: String(x?.properties?.context || "").split(",")[0]?.trim() || null,
                  address: {
                    county: x?.properties?.city || null,
                    state: x?.properties?.context || null,
                  },
                }))
                .filter((x) => x?.label)
            : [])
        : (Array.isArray(json)
            ? json
                .map((x) => ({
                  label: toShortLabel(x?.address) || x?.display_name,
                  place_id: x?.place_id,
                  postcode: x?.address?.postcode ?? null,
                  citycode: null,
                  depCodeFromContext: null,
                  address: x?.address ?? null,
                }))
                .filter((x) => x?.label)
            : []);
      setAddrSuggestionsByKey((prev) => ({ ...prev, [fieldKey]: items }));
    } catch {
      if (reqId !== addrRequestIdRef.current) return;
      setAddrSuggestionsByKey((prev) => ({ ...prev, [fieldKey]: [] }));
    }
  };

  const refusePasteIfCoord = (e) => {
    const t = (e.clipboardData || window.clipboardData)?.getData("text/plain");
    if (t != null && String(t).length > 0 && texteContientCoordonnees(t)) {
      e.preventDefault();
      toast.error(messageCoordonneesInterdites);
    }
  };

  const refuseDropIfCoord = (e) => {
    const t = e.dataTransfer?.getData("text/plain");
    if (t != null && String(t) !== "" && texteContientCoordonnees(t)) {
      e.preventDefault();
      toast.error(messageCoordonneesInterdites);
    }
  };

  const doSubmit = async () => {
    if (!user) {
      saveNouveauDossierDraft({
        form,
        secteurFiltre,
        champsSelectionnesRecherche,
        criteresRequisRecherche,
      });
      toast.info("Inscrivez-vous ou connectez-vous pour publier votre annonce.");
      navigate("/register", { state: { from: { pathname: "/nouveau-dossier", search: "" } } });
      return;
    }
    if (texteContientCoordonnees(form.title) || texteContientCoordonnees(form.description)) {
      toast.error(messageCoordonneesInterdites);
      return;
    }
    if (form.type === "recherche" && selectedProduit) {
      if (!Array.isArray(champsSelectionnesRecherche) || champsSelectionnesRecherche.length === 0) {
        toast.error("Ajoutez au moins un critère (champ) à votre recherche.");
        return;
      }
    }
    const champsPourValidation = form.type === "recherche" ? champsAffichesRecherche : champsAffiches;
    if (selectedProduit && champsPourValidation.length > 0) {
      const miss = messageFicheIncomplete(form.custom_fields, champsPourValidation);
      if (miss) {
        toast.error(miss);
        return;
      }
      const phoneErr = findInvalidPhoneInFiche(form.custom_fields, champsPourValidation);
      if (phoneErr) {
        toast.error(phoneErr);
        return;
      }
    }
    // Prix : vente = prix unique ; recherche = fourchette min/max.
    let pr = parseFloat(form.price);
    let prMin = null;
    let prMax = null;
    if (form.type === "recherche") {
      prMin = parseFloat(form.price_min);
      prMax = parseFloat(form.price_max);
      if (!Number.isFinite(prMin) || prMin < 1) {
        toast.error("Indiquez un budget minimum d’au moins 1 €.");
        return;
      }
      if (!Number.isFinite(prMax) || prMax < 1) {
        toast.error("Indiquez un budget maximum d’au moins 1 €.");
        return;
      }
      if (prMax < prMin) {
        toast.error("Le budget maximum doit être supérieur ou égal au budget minimum.");
        return;
      }
      // Compat : la colonne `price` est utilisée par le listing (tri/filtres) → on y met le max.
      pr = prMax;
    } else {
      if (!Number.isFinite(pr) || pr < 1) {
        toast.error("Indiquez un montant d’au moins 1 €.");
        return;
      }
    }
    setLoading(true);
    try {
      const syncedFields = syncDeptFieldsFromAddressIntl(
        form.custom_fields,
        champsPourValidation,
        getSelectedCountryValue,
      );
      const formForSave =
        syncedFields !== form.custom_fields ? { ...form, custom_fields: syncedFields } : form;
      const { location: _loc, client_name: _cn, client_phone: _cp, ...formRest } = formForSave;
      const autoTitle = () => {
        const p = selectedProduit?.nom ? String(selectedProduit.nom).trim() : "";
        return p ? `RDV — ${p}` : "RDV";
      };
      const payload = {
        ...formRest,
        status: "online",
        hors_saas: false,
        price: pr,
        publication_certified_at: new Date().toISOString(),
      };
      if (form.type === "recherche") {
        payload.price_min = prMin;
        payload.price_max = prMax;
      } else {
        payload.price_min = null;
        payload.price_max = null;
      }
      if (form.type === "vente") {
        payload.title = autoTitle();
      }
      if (form.type === "vente" && selectedProduit) {
        const d = deriveVenteDossierDepuisFiche(formForSave.custom_fields, champsAffiches);
        payload.location = d.location;
        if (d.client_name != null) payload.client_name = d.client_name;
        if (d.client_phone != null) payload.client_phone = d.client_phone;
      } else if (form.type === "recherche" && selectedProduit) {
        const d = deriveVenteDossierDepuisFiche(formForSave.custom_fields, champsAffichesRecherche);
        const loc = d.location;
        if (!loc || String(loc).trim() === "—" || String(loc).trim() === "") {
          toast.error("Ajoutez un champ de zone/localisation à votre recherche.");
          setLoading(false);
          return;
        }
        const deptInfo = findDepartementFieldRaw({ custom_fields: payload.custom_fields });
        const deptFormatted =
          formatDepartmentDisplay(deptInfo.raw || loc) || String(loc || "").trim() || null;
        if (deptInfo.key && deptFormatted) {
          payload.custom_fields = { ...payload.custom_fields, [deptInfo.key]: deptFormatted };
        }
        payload.location = deptFormatted;
      } else {
        payload.location = form.location;
        if (form.client_name) payload.client_name = form.client_name;
        if (form.client_phone) payload.client_phone = form.client_phone;
      }
      if (selectedProduit) payload.secteur = selectedProduit.secteur;
      if (form.type === "recherche") {
        payload.recherche_criteres_requis = criteresRequisRecherche;
      }
      await api.post("/dossiers", normalizePhoneFieldsInDossierPayload(payload));
      clearNouveauDossierDraft();
      toast.success("Annonce publiée");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (texteContientCoordonnees(form.title) || texteContientCoordonnees(form.description)) {
      toast.error(messageCoordonneesInterdites);
      return;
    }
    if (!user) {
      saveNouveauDossierDraft({
        form,
        secteurFiltre,
        champsSelectionnesRecherche,
        criteresRequisRecherche,
      });
      toast.info("Inscrivez-vous ou connectez-vous pour publier votre annonce.");
      navigate("/register", { state: { from: { pathname: "/nouveau-dossier", search: "" } } });
      return;
    }
    if (form.type === "vente") {
      setVenteDisclaimerOpen(true);
      return;
    }
    doSubmit();
  };

  const isRecherche = form.type === "recherche";

  const deriveAchat = useMemo(
    () =>
      form.type === "recherche" && selectedProduit
        ? deriveVenteDossierDepuisFiche(form.custom_fields, champsAffichesRecherche)
        : null,
    [form.type, form.custom_fields, champsAffichesRecherche, selectedProduit]
  );
  const hasLieuDepuisFicheAchat = Boolean(
    deriveAchat?.location &&
      String(deriveAchat.location).trim() &&
      String(deriveAchat.location).trim() !== "—"
  );
  const ficheBlockVisible = Boolean(selectedProduit && champsAffiches.length > 0);
  const etapeFiche = 2;
  const etapeAnnonce = ficheBlockVisible ? 3 : 2;

  const sellerPayoutHours = Math.max(
    0,
    SELLER_PAYOUT_HOURS,
  );

  const memberLayout = Boolean(user);
  const publishTitle = isRecherche ? "Nouvelle annonce (recherche)" : "Nouvelle annonce (proposition)";
  const formScrollClass = memberLayout
    ? "max-h-[min(72vh,760px)] overflow-y-auto overscroll-contain"
    : "h-full overflow-y-auto overscroll-contain";

  const formBlock = (
    <>
          {!memberLayout ? (
          <header className="mb-4 sm:mb-5">
            <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-[#0B0D12]">
              {publishTitle}
            </h1>
          </header>
          ) : null}

          <form onSubmit={submit} className={memberLayout ? "" : "min-h-0 flex-1 overflow-hidden"}>
            <div className={formScrollClass} data-testid="nouveau-dossier-scroll-panel">
              <div className="overflow-clip rounded-2xl border border-slate-200/80 bg-white shadow-sm shadow-slate-900/[0.04] divide-y divide-slate-100">
          <FormBlock n={1}>
            <div>
              <Label className={labelClass} htmlFor="dossier-type" required>
                Vous voulez
              </Label>
              <div
                id="dossier-type"
                className="grid grid-cols-2 gap-2 rounded-2xl border border-slate-200/90 bg-slate-50/70 p-2"
                role="group"
                aria-label="Vous voulez"
                data-testid="dossier-type-segmented"
              >
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, type: "vente", description: "" }))}
                  aria-pressed={form.type === "vente"}
                  data-testid="dossier-type-vente"
                  className={cn(
                    "h-11 rounded-xl text-sm font-semibold transition",
                    form.type === "vente"
                      ? "bg-[#2D7FF9] text-white shadow-sm"
                      : "bg-white text-slate-900 hover:bg-slate-50 ring-1 ring-slate-200/80"
                  )}
                >
                  Vendre
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, type: "recherche" }))}
                  aria-pressed={form.type === "recherche"}
                  data-testid="dossier-type-recherche"
                  className={cn(
                    "h-11 rounded-xl text-sm font-semibold transition",
                    form.type === "recherche"
                      ? "bg-[#2D7FF9] text-white shadow-sm"
                      : "bg-white text-slate-900 hover:bg-slate-50 ring-1 ring-slate-200/80"
                  )}
                >
                  Acheter
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-3">
              <div>
                <Label className={labelClass} htmlFor="dossier-secteur-trigger" required>
                  Secteur
                </Label>
                <Select value={secteurFiltre || undefined} onValueChange={setSecteurFiltre}>
                  <SelectTrigger id="dossier-secteur-trigger" className="h-11 w-full text-base" data-testid="dossier-secteur-select">
                    <SelectValue placeholder="Choisir" />
                  </SelectTrigger>
                  <SelectContent className="z-[100]">
                    {secteursUniques.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelClass} htmlFor="dossier-produit-trigger" required>
                  Produit
                </Label>
                <Select
                  value={form.produit_id}
                  onValueChange={(v) => setForm((f) => ({ ...f, produit_id: v, custom_fields: {} }))}
                  disabled={!secteurFiltre}
                >
                  <SelectTrigger id="dossier-produit-trigger" className="h-11 w-full text-base" data-testid="dossier-produit-select">
                    <SelectValue
                      placeholder={secteurFiltre ? "Choisir" : "Choisir d’abord le secteur"}
                    />
                  </SelectTrigger>
                  <SelectContent className="z-[100]">
                    {produitsFiltres.map((p) => (
                      <SelectItem key={p.produit_id} value={p.produit_id}>{p.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </FormBlock>

          {ficheBlockVisible && (
            <FormBlock
              n={isRecherche ? null : etapeFiche}
              variant="muted"
              title={isRecherche ? "Champs à inclure dans la recherche" : null}
            >
              {isRecherche && (
                <div className="rounded-2xl border border-slate-200/90 bg-white p-4 sm:p-5 shadow-sm space-y-3">
                  <div className="space-y-1.5">
                    <Select
                      value={""}
                      onValueChange={(v) => {
                        if (!v) return;
                        setChampsSelectionnesRecherche((prev) => {
                          const s = new Set((prev || []).map((x) => String(x)));
                          s.add(String(v));
                          return Array.from(s);
                        });
                      }}
                    >
                      <SelectTrigger
                        id="recherche-add-champ"
                        className="h-11 text-base"
                        data-testid="recherche-add-champ"
                        aria-label="Ajouter un champ à inclure dans la recherche"
                      >
                        <SelectValue placeholder="Ajouter un champ…" />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        {champsSelectionnablesRecherche
                          .map((c) => c?.nom)
                          .filter(Boolean)
                          .filter((nom) => !(champsSelectionnesRecherche || []).includes(nom))
                          .map((nom) => (
                            <SelectItem key={nom} value={nom}>{nom}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {(champsSelectionnesRecherche?.length ?? 0) > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {champsSelectionnesRecherche.map((nom) => (
                        <button
                          key={nom}
                          type="button"
                          onClick={() => setChampsSelectionnesRecherche((prev) => (prev || []).filter((x) => x !== nom))}
                          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-800 hover:bg-slate-100"
                          data-testid={`recherche-selected-${nom}`}
                          aria-label={`Retirer le champ ${nom}`}
                          title="Cliquer pour retirer"
                        >
                          <span className="truncate max-w-[16rem]">{nom}</span>
                          <span className="text-slate-500" aria-hidden>×</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-amber-900/90 bg-amber-50/90 rounded-xl py-3 px-3.5 border border-amber-200/80">
                      Ajoutez au moins un champ pour définir votre recherche.
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-4 sm:space-y-5">
              {(() => {
                const champsCourants = isRecherche ? champsAffichesRecherche : champsAffiches;
                const hasAdresseFieldInSchema = champsCourants.some((cc) => /adresse|address/i.test(String(cc?.nom || "")));
                return champsCourants.map((c, fieldIdx) => {
                const listOptions = Array.isArray(c.options)
                  ? c.options.map((o) => String(o).trim()).filter(Boolean)
                  : [];
                const listVal = form.custom_fields[c.nom];
                const listSelectValue =
                  listVal != null && listVal !== "" && listOptions.includes(String(listVal)) ? String(listVal) : undefined;
                const nomChamp = String(c.nom || "");
                const isDepartement = /département|departement/i.test(nomChamp);
                const isAdresse = /adresse|address/i.test(nomChamp);
                const isCredit = /cr[ée]dit/i.test(nomChamp);
                const isRevenu = /revenu|revenue/i.test(nomChamp);
                const isPrecisionField = isPrecisionFieldName(nomChamp);
                const isContactField = isContactLikeChamp(nomChamp);
                const isSituationProList = isSituationProListField(nomChamp);
                const situationProPrecNom = isSituationProList ? precisionFieldNameForSituationPro(nomChamp) : null;
                if (isSituationProPrecisionField(nomChamp)) return null;
                if (shouldHideSituationProField(nomChamp, form.custom_fields, champsCourants)) return null;
                const periodeKey = `${nomChamp}__periode`;
                const periodeValue = form.custom_fields?.[periodeKey] || "mois";
                return (
                // Si la fiche contient un champ adresse (France), le département est auto : on ne l'affiche pas.
                (isRecherche && isContactField) ? null : (
                (isDepartement && hasAdresseFieldInSchema) ? null : (
                <div key={c.champ_id ?? c.nom} className={ficheChampClass}>
                  <div className="space-y-1.5">
                  <div className="flex items-start justify-between gap-3">
                    <Label className={labelClass} required={!isPrecisionField}>
                      {c.nom}
                      {c.visible_public === false && (
                        <span className="ml-1.5 block sm:inline text-sm text-destructive font-semibold">(non affiché sur l’annonce publique)</span>
                      )}
                    </Label>
                    {isRecherche && c?.nom ? (
                      <button
                        type="button"
                        onClick={() =>
                          setChampsSelectionnesRecherche((prev) => (prev || []).filter((x) => x !== c.nom))
                        }
                        className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200/80 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
                        aria-label={`Retirer le champ ${c.nom}`}
                        title="Retirer ce champ"
                        data-testid={`recherche-remove-${c.nom}`}
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                  {isDepartement && isRecherche ? (
                    <div className="space-y-1">
                      <Input
                        required={!isPrecisionField}
                        value={form.custom_fields[nomChamp] || ""}
                        onChange={(e) => updateCustom(nomChamp, e.target.value)}
                        className="h-11 text-base font-mono tabular-nums"
                        data-testid={`custom-${nomChamp}`}
                        placeholder="Ex. 63, 43, 03 ou 63-43-03"
                      />
                      <p className="text-[11px] text-slate-500">
                        Plusieurs départements possibles (virgules, espaces ou tirets).
                      </p>
                    </div>
                  ) : isDepartement && deptOptions.length > 0 ? (
                    <Select
                      value={form.custom_fields[nomChamp] || ""}
                      onValueChange={(v) => updateCustom(nomChamp, v)}
                      required={!isPrecisionField}
                    >
                      <SelectTrigger className="h-11 text-base" data-testid={`custom-${nomChamp}`}>
                        <SelectValue placeholder="Choisir un département" />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        {deptOptions.map((d) => (
                          <SelectItem key={d.code} value={d.code}>
                            {d.code} — {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : isAdresse && c.type === "text" ? (
                    <div
                      className="relative"
                      ref={(el) => {
                        if (el) addrWrapRefs.current[nomChamp] = el;
                        else delete addrWrapRefs.current[nomChamp];
                      }}
                    >
                      <Input
                        required={!isPrecisionField}
                        value={form.custom_fields[nomChamp] || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          updateCustom(nomChamp, v);
                          void fetchAddressSuggestions(nomChamp, v);
                        }}
                        className="h-11 text-base"
                        data-testid={`custom-${nomChamp}`}
                        autoComplete="off"
                        placeholder={
                          countryToIso2(getSelectedCountryValue)
                            ? "Rechercher une adresse…"
                            : "Sélectionnez d’abord le pays"
                        }
                      />
                    </div>
                  ) : (isCredit || isRevenu) && c.type === "number" ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr,10rem] sm:items-end">
                      <Input
                        type="text"
                        inputMode="decimal"
                        required={!isPrecisionField}
                        value={decimalFieldDisplay(form.custom_fields[nomChamp])}
                        onChange={(e) => updateCustom(nomChamp, e.target.value)}
                        className="h-11 text-base font-mono tabular-nums"
                        data-testid={`custom-${nomChamp}`}
                      />
                      <Select
                        value={periodeValue}
                        onValueChange={(v) => updateCustom(periodeKey, v)}
                      >
                        <SelectTrigger className="h-11 text-base" data-testid={`custom-${nomChamp}-periode`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[100]">
                          <SelectItem value="mois">par mois</SelectItem>
                          <SelectItem value="an">par an</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <>
                      {c.type === "text" && (
                        isPhoneLikeChampName(nomChamp) ? (
                          <Input
                            type="tel"
                            inputMode="tel"
                            required={!isPrecisionField}
                            value={form.custom_fields[nomChamp] || ""}
                            onChange={(e) => updateCustom(nomChamp, formatPhoneInputLive(e.target.value))}
                            className="h-11 text-base font-mono tabular-nums"
                            data-testid={`custom-${nomChamp}`}
                          />
                        ) : (
                          <Input
                            required={!isPrecisionField}
                            value={form.custom_fields[nomChamp] || ""}
                            onChange={(e) => updateCustom(nomChamp, e.target.value)}
                            className="h-11 text-base"
                            data-testid={`custom-${nomChamp}`}
                          />
                        )
                      )}
                      {c.type === "number" &&
                        (isPhoneLikeChampName(nomChamp) ? (
                          <Input
                            type="tel"
                            inputMode="tel"
                            required={!isPrecisionField}
                            value={form.custom_fields[nomChamp] || ""}
                            onChange={(e) => updateCustom(nomChamp, formatPhoneInputLive(e.target.value))}
                            className="h-11 text-base font-mono tabular-nums"
                            data-testid={`custom-${nomChamp}`}
                          />
                        ) : (
                          <Input
                            type="text"
                            inputMode="decimal"
                            required={!isPrecisionField}
                            value={decimalFieldDisplay(form.custom_fields[nomChamp])}
                            onChange={(e) => updateCustom(nomChamp, e.target.value)}
                            className="h-11 text-base font-mono tabular-nums"
                            data-testid={`custom-${nomChamp}`}
                          />
                        ))}
                    </>
                  )}
                  {c.type === "list" && (
                    listOptions.length === 0 ? (
                      <p className="text-sm text-amber-950 bg-amber-50 border border-amber-200/90 rounded-xl px-3.5 py-3">
                        Aucun choix défini pour ce champ. L’administrateur doit indiquer les options (produit : type « menu déroulant »
                        + liste des valeurs).
                      </p>
                    ) : (
                    <>
                    <Select
                      value={listSelectValue}
                      onValueChange={(v) => {
                        updateCustom(c.nom, v);
                        if (!isAutreSituationProValue(v) && situationProPrecNom) {
                          updateCustom(situationProPrecNom, "");
                        }
                        if (isSituationMaritaleField(nomChamp) && isCelibataireMaritalValue(v)) {
                          for (const ch of champsCourants) {
                            if (!ch?.nom) continue;
                            if (!isSituationProListField(ch.nom) && !isSituationProPrecisionField(ch.nom)) {
                              continue;
                            }
                            updateCustom(ch.nom, "");
                          }
                          setCriteresRequisRecherche((prev) =>
                            (prev || []).filter(
                              (nom) => !isSituationProListField(nom) && !isSituationProPrecisionField(nom),
                            ),
                          );
                        }
                      }}
                      required={!isPrecisionField && c.required !== false}
                    >
                      <SelectTrigger className="h-11 text-base" data-testid={`custom-${c.nom}`}>
                        <SelectValue placeholder="Choisir une option" />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        {listOptions.map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isSituationProList && isAutreSituationProValue(listVal) && situationProPrecNom ? (
                      <div className="mt-2 space-y-1.5">
                        <Label className="text-xs text-slate-600" required>
                          Précisez (Autre)
                        </Label>
                        <Input
                          required
                          value={form.custom_fields[situationProPrecNom] || ""}
                          onChange={(e) => updateCustom(situationProPrecNom, e.target.value)}
                          className="h-11 text-base"
                          placeholder="Ex. indépendant, retraité, intérim…"
                          data-testid={`custom-${situationProPrecNom}`}
                        />
                      </div>
                    ) : null}
                    </>
                    )
                  )}
                  {c.type === "boolean" && (
                    <div className="flex min-h-11 items-center gap-3">
                      <Switch checked={!!form.custom_fields[c.nom]} onCheckedChange={(v) => updateCustom(c.nom, v)} data-testid={`custom-${c.nom}`} />
                      <span className="text-sm font-medium text-slate-800">{form.custom_fields[c.nom] ? "Oui" : "Non"}</span>
                    </div>
                  )}
                  {isRecherche && c.nom && (
                    <div
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-slate-200/90 bg-slate-50/90 px-3.5 py-3 sm:py-2.5"
                      id={`recherche-hint-${fieldIdx}`}
                    >
                      <span className="text-sm font-medium text-slate-800 leading-snug pr-0 sm:pr-3">
                        Exiger la <span className="whitespace-nowrap">même réponse</span> côté vendeur pour ce critère
                      </span>
                      <div className="flex shrink-0 items-center justify-between gap-3 sm:justify-end border-t border-slate-200/80 pt-3 sm:border-0 sm:pt-0">
                        <span className="text-sm text-slate-500 sm:sr-only">Activer le filtre</span>
                        <Switch
                          checked={criteresRequisRecherche.includes(c.nom)}
                          onCheckedChange={(on) => setCritereRequisPourMatch(c.nom, on)}
                          data-testid="recherche-critere-match-switch"
                          aria-label={`Même réponse côté vendeur pour le critère : ${c.nom}`}
                        />
                      </div>
                    </div>
                  )}
                  </div>
                </div>
                )));
                });
              })()}
              </div>
            </FormBlock>
          )}

          <FormBlock n={etapeAnnonce}>
            {isRecherche && (
              <div>
                <Label className={labelClass} htmlFor="title" required>
                  Titre
                </Label>
                <Input
                  id="title"
                  required
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  onPaste={refusePasteIfCoord}
                  className="h-11 text-base"
                  data-testid="dossier-title-input"
                  placeholder="Une phrase claire, lisible d’un coup d’œil"
                />
              </div>
            )}
            {isRecherche ? (
              <div>
                <Label className={labelClass} htmlFor="desc">
                  Précision : Texte de l’annonce{" "}
                  <span className="font-normal text-slate-500">(optionnel)</span>
                </Label>
                <Textarea
                  id="desc"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  onPaste={refusePasteIfCoord}
                  onDrop={refuseDropIfCoord}
                  className="min-h-[6.5rem] resize-y text-base leading-relaxed"
                  data-testid="dossier-description-input"
                  placeholder="Contexte, attente, utile aux vendeurs qui lisent votre recherche (sans coordonnées)."
                />
              </div>
            ) : null}

            {isRecherche ? (
              <div className="space-y-2.5 min-h-[4.5rem]">
                {hasLieuDepuisFicheAchat && deriveAchat && (
                  <p className="text-sm sm:text-[15px] text-slate-800 border border-slate-200/90 rounded-xl px-3.5 py-3 bg-slate-50/90">
                    Zone retenue : <strong className="text-slate-950 font-semibold">{deriveAchat.location}</strong>
                  </p>
                )}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-end">
                  <div>
                    <Label className={labelClass} htmlFor="price-min" required>
                      Budget min (€)
                    </Label>
                    <Input
                      id="price-min"
                      type="text"
                      inputMode="decimal"
                      required
                      value={decimalFieldDisplay(form.price_min)}
                      onChange={(e) => setForm((f) => ({ ...f, price_min: e.target.value }))}
                      className="h-11 text-base font-mono tabular-nums"
                      data-testid="dossier-price-min-input"
                    />
                  </div>
                  <div>
                    <Label className={labelClass} htmlFor="price-max" required>
                      Budget max (€)
                    </Label>
                    <Input
                      id="price-max"
                      type="text"
                      inputMode="decimal"
                      required
                      value={decimalFieldDisplay(form.price_max)}
                      onChange={(e) => setForm((f) => ({ ...f, price_max: e.target.value }))}
                      className="h-11 text-base font-mono tabular-nums"
                      data-testid="dossier-price-max-input"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2.5">
                <div className="w-full sm:max-w-xs">
                  <Label className={labelClass} htmlFor="price-vente" required>
                    Prix (€) pour le créneau
                  </Label>
                  <Input
                    id="price-vente"
                    type="text"
                    inputMode="decimal"
                    required
                    value={decimalFieldDisplay(form.price)}
                    onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                    className="h-11 text-base font-mono tabular-nums"
                    data-testid="dossier-price-input"
                  />
                </div>
              </div>
            )}
          </FormBlock>
              </div>
              <div className="sticky bottom-0 z-10 border-t border-slate-200/70 bg-white/95 px-2 pt-2 pb-2 backdrop-blur space-y-2">
                <Button
                  type="submit"
                  disabled={loading || !form.produit_id || !secteurFiltre}
                  className="btn-primary w-full h-11 rounded-xl text-sm sm:text-base font-semibold"
                  data-testid="submit-dossier"
                >
                  {loading ? "Publication…" : "Publier l’annonce"}
                </Button>
              </div>
            </div>
          </form>
    </>
  );

  const overlays = (
    <>
      {addrSuggestOverlay &&
        typeof document !== "undefined" &&
        (addrSuggestionsByKey[addrSuggestOverlay.fieldKey] || []).length > 0 &&
        createPortal(
          <div
            data-testid="address-suggest-portal"
            role="listbox"
            aria-label="Suggestions d’adresse"
            className="fixed z-[300] max-h-[min(40vh,22rem)] max-w-[calc(100vw-1.5rem)] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg"
            style={{
              top: addrSuggestOverlay.top,
              left: addrSuggestOverlay.left,
              width: Math.max(addrSuggestOverlay.width || 0, 220),
            }}
          >
            {(addrSuggestionsByKey[addrSuggestOverlay.fieldKey] || []).map((it) => {
              const fk = addrSuggestOverlay.fieldKey;
              return (
                <button
                  key={String(it.place_id || it.label)}
                  type="button"
                  role="option"
                  className="block w-full px-3.5 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                  onClick={() => {
                    updateCustom(fk, it.label);
                    setDepartementFromAddressSelection({
                      postcode: it.postcode,
                      citycode: it.citycode,
                      depCodeFromContext: it.depCodeFromContext,
                      addressDetails: it.address,
                      fullAddressLine: it.label,
                    });
                    setAddrSuggestionsByKey((prev) => ({ ...prev, [fk]: [] }));
                  }}
                >
                  {it.label}
                </button>
              );
            })}
          </div>,
          document.body,
        )}

      <VenteListingDisclaimerDialog
        open={venteDisclaimerOpen}
        onOpenChange={setVenteDisclaimerOpen}
        confirmLoading={loading}
        onConfirm={() => {
          setVenteDisclaimerOpen(false);
          void doSubmit();
        }}
      />
    </>
  );

  if (memberLayout) {
    return (
      <>
        <MemberShell
          title={publishTitle}
          subtitle="Publiez une annonce vente ou recherche : la fiche sera visible sur la marketplace."
          dashboardNav={MEMBER_DASH_NAV_BASE}
        >
          <div className="mx-auto w-full max-w-xl">{formBlock}</div>
        </MemberShell>
        {overlays}
      </>
    );
  }

  return (
    <div className="h-[100dvh] overflow-hidden bg-gradient-to-b from-slate-50/90 to-slate-100/40">
      <Navbar />
      <div className="mx-auto flex h-[calc(100dvh-4rem)] max-w-7xl flex-col px-4 pt-6 sm:px-6 sm:pt-8">
        <div className="mx-auto flex w-full max-w-xl flex-1 flex-col min-h-0">{formBlock}</div>
      </div>
      {overlays}
    </div>
  );
};

export default NouveauDossier;
